import i18n from '@/libs/i18n'
import type { ApiError } from '@/types/Common'

/** Mirrors the error codes naloge-api answers with (docs/API.md → Errors). */
export enum Errors {
    InternalServerError    = 1,
    InvalidData            = 2,
    NotFound               = 3,
    Unauthorized           = 4,
    FailedToInsert         = 5,
    Forbidden              = 6,
    UsernameTaken          = 7,
    LastAdmin              = 8,
    PasswordTooShort       = 9,
    WrongPassword          = 10,
    TooManyAttempts        = 11,
    PasskeysUnavailable    = 12,
    ProjectKeyTaken        = 13,
    AssigneeNotMember      = 14,
    DependencyCycle        = 15,
    DependencyOtherProject = 16,
    MailNotConfigured      = 17,
    MailFailed             = 18,
    FileTooLarge           = 19,
    // 20–23 are retired and never reused (docs/API.md → Errors).
}

/** The code of an API error body, or 0 when it carries none. */
export const errorCode = (errorObject: unknown): number => {
    const error = (errorObject as ApiError)?.error

    return typeof error === 'object' && error ? error.code ?? 0 : 0
}

/** The server's own message of an API error body, trimmed. */
export const errorMessage = (errorObject: unknown): string => {
    const error = (errorObject as ApiError)?.error

    if (typeof error === 'string') return error.trim()

    return (error?.message ?? '').trim()
}

/*
 * parseError turns an API error body into a sentence.
 *
 * The web translates by CODE, never by message (docs/API.md → Errors): the server's messages
 * are English and meant for a log. A code this console has no sentence for gets the caller's
 * fallback, which names what was being attempted.
 */
export const parseError = (errorObject: unknown, fallback = ''): string => {
    const t = i18n.global.t

    switch (errorCode(errorObject)) {
        case Errors.InvalidData:     return t('errors.invalid_data')
        case Errors.NotFound:        return t('errors.not_found')
        case Errors.Unauthorized:    return t('errors.unauthorized')
        case Errors.Forbidden:       return t('errors.forbidden')
        case Errors.TooManyAttempts: return t('auth.too_many_attempts')
        default:                     return fallback || t('errors.generic_error')
    }
}

/*
 * parseKnownError is parseError plus the refusals a person can actually cause — a taken
 * username or project key, the last administrator, a short or wrong password, a dependency
 * that would loop, an assignee from outside the project, a file that is too big, a mail
 * server that is not set up or said no. Each is a sentence the console already knows how to
 * say in the reader's language. A refused mail keeps the server's reply, which is the only
 * part of that answer worth reading.
 */
export const parseKnownError = (errorObject: unknown, fallback = ''): string => {
    const t = i18n.global.t

    switch (errorCode(errorObject)) {
        case Errors.UsernameTaken:          return t('user.username_taken')
        case Errors.LastAdmin:              return t('user.last_admin')
        case Errors.PasswordTooShort:       return t('user.password_too_short')
        case Errors.WrongPassword:          return t('password.wrong_current')
        case Errors.PasskeysUnavailable:    return t('passkey.unavailable')
        case Errors.ProjectKeyTaken:        return t('project.key_taken')
        case Errors.AssigneeNotMember:      return t('task.assignee_not_member')
        case Errors.DependencyCycle:        return t('task.dependency_cycle')
        case Errors.DependencyOtherProject: return t('task.dependency_other_project')
        case Errors.MailNotConfigured:      return t('mail.not_configured')
        case Errors.MailFailed: {
            const reply = errorMessage(errorObject)

            return reply ? t('mail.failed_reply', { reply }) : t('mail.failed')
        }
        case Errors.FileTooLarge:           return t('attachment.too_large')
        default:                            return parseError(errorObject, fallback)
    }
}
